import { ConfirmationResult, getAdditionalUserInfo } from "firebase/auth";
import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { CountryIndicator } from "@domain/currentProfile/countriesIndicators";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Description } from "@ui/components/containers/description";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Title } from "@ui/components/containers/title";
import { MemberIcon, PhoneNumberIcon } from "@ui/components/icons";
import { IndicatorDataList } from "@ui/components/inputs/indicatorDataList";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { Row, Stack, row, stack } from "@ui/components/layout";

interface PhoneNumberAuthModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
}

export const PhoneNumberAuthModal: React.FC<PhoneNumberAuthModalProps> = ({ close }) => {
	const [primaryName, setPrimaryName] = useState<string>("");
	const [secondaryName, setSecondaryName] = useState<string>("");
	const [phoneNumber, setPhoneNumber] = useState<string>("");
	const [isNewUser, setIsNewUser] = useState(false);
	const [indicator, setIndicator] = useState<CountryIndicator>("221");
	const [code, setCode] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [confirmObj, setConfirmObj] = useState<ConfirmationResult | undefined>(undefined);
	const [isCodeResend, setIsCodeResend] = useState<boolean>(false);
	const { currentProfileService } = useServices();

	const { run: setupRecaptcha, loading: recaptchaLoading } = useAsync(
		async (phoneNumber: string, isRecaptchaDisplay?: boolean) => {
			try {
				const setup = await currentProfileService.setupRecaptcha(phoneNumber, isRecaptchaDisplay);
				if (setup) {
					setIsCodeResend(true);
					setConfirmObj(setup.confirm);
					setup.recaptcha.clear();
				}
			} catch (error) {
				console.log("error", error);
			}
		},
		[]
	);

	const { run: loginWithPhoneNumber } = useAsync(async (confirmObj: ConfirmationResult, code: string) => {
		try {
			setLoading(true);
			const result = await currentProfileService.checkUserWithPhoneNumber(confirmObj, code);
			console.log(result);
			const additionalUserInfo = getAdditionalUserInfo(result);
			if (!additionalUserInfo?.isNewUser) {
				close();
				return;
			}
			setIsNewUser(true);
			setPhoneNumber(result.user?.phoneNumber as string);
		} catch (error) {
			console.log("error", error);
		} finally {
			setLoading(false);
		}
	}, []);

	const { run } = useAsync(async () => {
		try {
			setLoading(true);

			await currentProfileService.loginWithPhoneNumber(phoneNumber, primaryName, secondaryName);
			close();
		} catch (error) {
			console.log("error", error);
			// set
		} finally {
			setLoading(false);
		}
	}, [phoneNumber, primaryName, secondaryName]);

	const isPhoneNumber = phoneNumber.length !== 0;
	const isCodeInvalid = code.length === 0;
	const isPrimaryNane = primaryName.length === 0;
	const isSecondaryName = secondaryName.length === 0;
	const lastTwoDigit = phoneNumber.slice(-2);

	const [seconds, setSeconds] = useState(30);
	const [count, setCount] = useState<number>(5);
	useEffect(() => {
		const interval = setInterval(() => {
			if (seconds > 0 && isCodeResend) {
				setSeconds(seconds - 1);
			}

			if (seconds === 0) {
				setIsCodeResend(false);
				clearInterval(interval);
			}
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [seconds, isCodeResend]);

	return (
		<Container close={close}>
			{!confirmObj ? (
				<>
					<div />
					<Stack gap={30}>
						<Row gap={40} align="flex-end" style={{ width: "100%" }}>
							<InfoEdition>
								<BlocInfos>
									<Title>{I18n.t("register.phonenumber.title")}</Title>
									<Description>{I18n.t("register.phonenumber.description")}</Description>
								</BlocInfos>
								<StyledBlocIndicator>
									<IndicatorDataList onChange={(value) => setIndicator(value)} value={indicator} />
									<InputPhone
										icon={<PhoneNumberIcon style={{ width: "14px", height: "14px" }} />}
										iconPosition="left"
										value={phoneNumber}
										onChange={(value) => {
											setPhoneNumber(value);
										}}
										placeholder={I18n.t("register.phonenumber.placeholder")}
									/>
								</StyledBlocIndicator>
								<Captcha>
									<div id="recaptcha-container" />
								</Captcha>
							</InfoEdition>
						</Row>
					</Stack>

					<LoadingButton
						loading={recaptchaLoading}
						disabled={!isPhoneNumber}
						onClick={() => {
							if (!phoneNumber) return;
							// setIsRecaptchaChecked(true);
							setupRecaptcha("+".concat("", indicator).concat("", phoneNumber));
						}}
					>
						{"Valider"}
					</LoadingButton>
				</>
			) : isNewUser ? (
				<>
					<Stack gap={30}>
						<Row gap={40} align="flex-end" style={{ width: "100%" }}>
							<InfoEdition>
								<BlocInfos>
									<Title>{I18n.t("register.phonenumber.title")}</Title>
									<Description>{I18n.t("register.phonenumber.description")}</Description>
								</BlocInfos>
								<InputPhone
									icon={<MemberIcon />}
									iconPosition="left"
									color="secondary"
									value={primaryName}
									placeholder={I18n.t("register.firstName.placeholder")}
									onChange={(value) => {
										setPrimaryName(value);
									}}
								/>
								<InputPhone
									icon={<MemberIcon />}
									iconPosition="left"
									color="secondary"
									value={secondaryName}
									placeholder={I18n.t("register.lastName.placeholder")}
									onChange={(value) => {
										setSecondaryName(value);
									}}
								/>
							</InfoEdition>
						</Row>
					</Stack>
					<LoadingButton onClick={() => run()} loading={loading} disabled={isPrimaryNane || isSecondaryName}>
						{I18n.t("register.button.submit")}
					</LoadingButton>
				</>
			) : (
				<>
					<InfoEditionOtp>
						<BlocInfo>
							<Title>{I18n.t("register.phonenumber.otp.title")}</Title>
							<Description>{I18n.t("register.phonenumber.otp.description", { lastTwoDigit })}</Description>
						</BlocInfo>
						<BlocOTP>
							<PrimaryMedium>{I18n.t("register.phonenumber.otp.label")}</PrimaryMedium>
							<InputWrapper>
								<OtpTextInput value={code} onChange={(e) => setCode(e.currentTarget.value)} />
							</InputWrapper>
						</BlocOTP>
					</InfoEditionOtp>
					<Captcha>
						<div id="recaptcha-container" />
					</Captcha>
					<LoadingButton
						onClick={() => loginWithPhoneNumber(confirmObj, code)}
						loading={loading}
						disabled={isCodeInvalid}
					>
						{I18n.t("confirm")}
					</LoadingButton>
					<Label>
						{I18n.t("code.message")}
						{isCodeResend ? (
							<Counter>00 : {seconds}</Counter>
						) : (
							<StyledTertiaryButton
								disabled={count === 0}
								onClick={() => {
									setSeconds(15);
									setCount((current) => current - 1);
									setConfirmObj(undefined);
								}}
							>
								{I18n.t("code.resend")}
							</StyledTertiaryButton>
						)}
					</Label>
				</>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};
	> button {
		margin: 0 auto;
	}
`;

const Label = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
	font-weight: 400;
	font-size: 14px;
	line-height: 26px;

	color: rgba(0, 0, 0, 0.5);
`;

const Captcha = styled.div`
	display: flex;
	width: 100%;
	position: relative;
	margin-left: 133px;
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 100%;
	align-content: justify;
`;
const InfoEditionOtp = styled.div`
	${stack(40, "center", "center")};
	width: 100%;
`;

const BlocInfos = styled.div`
	${stack(30)};
	width: 100%;
	position: relative;
	left: 10px;
`;
const BlocInfo = styled.div`
	${stack(20)};
	width: 100%;
`;
const BlocOTP = styled.div`
	${stack(10)};
	width: 100%;
`;

const StyledBlocIndicator = styled.div`
	${row(5, "flex-end", "center")}
	display: grid;
	grid-template-columns: 100px 1fr;
	width: 98%;
	grid-gap: 10px;
	position: relative;
	left: 10px;

	@media ${mobile} {
		${stack(8, "center", "baseline")}
	}
`;

const InputPhone = styled(SimpleTextInput)<{ isInvalid?: boolean }>`
	border-radius: 10px;

	font-size: 0.935rem;
	outline: none;
	@media ${desktop} {
		padding: 10px 12px 9px;
	}
	:disabled {
		color: ${({ theme }) => theme.colors.textTertiary};
	}

	:focus {
		border: 1px solid ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.secondary)};
	}

	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}

	@media (max-width: 370px) {
		width: 140px;
	}
`;

const InputWrapper = styled.div`
	display: flex;
	align-items: center;
	width: 300px;
	padding: 10px 12px 9px;
	border-radius: 10px;
	border: 1px solid ${({ theme }) => theme.colors.textTertiary};
	padding-left: 0.5rem;
	overflow: hidden;

	@media (max-width: 370px) {
		width: 140px;
	}

	:focus-within {
		border: 1px solid ${({ theme }) => theme.colors.secondary};
	}
`;

const OtpTextInput = styled.input<{ isInvalid?: boolean }>`
	flex-grow: 1;
	font-size: 0.935rem;
	outline: none;
	border: none;
`;

const StyledTertiaryButton = styled(TertiaryButton)`
	color: ${({ theme }) => theme.colors.secondary};
`;

const Counter = styled.div`
	color: ${({ theme }) => theme.colors.secondary};
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 26px;
`;
